import i18n, { type ResourceLanguage } from 'i18next'
import { initReactI18next } from 'react-i18next'

import enBundle from './locales/en.json'
import idBundle from './locales/id.json'
import tetBundle from './locales/tet.json'
import { readLocale, type AppLocale } from './utils/locale'

// Strip any /ppob suffix that may be baked into the configured base
// URL — the i18n bundle endpoint lives at /api/v1/i18n, outside the
// PPOB prefix. Mirrors api.ts's address/profile helpers.
const RAW_BASE = process.env.REACT_APP_API_BASE_URL || ''
const API_BASE = RAW_BASE.replace(/\/ppob\/?$/, '')
const APP_SCOPE = 'voucher_game'

// `tet` ships seeded from id (every key starts in Bahasa) and gets
// translated string-by-string over time. Untranslated keys fall back
// to id via fallbackLng so the customer never sees an untranslated
// `key.path` placeholder mid-rollout.
const baseBundles: Record<AppLocale, ResourceLanguage> = {
  en: enBundle as ResourceLanguage,
  id: idBundle as ResourceLanguage,
  tet: tetBundle as ResourceLanguage,
}

/**
 * Fetches admin-edited overrides from the backend bundle endpoint and
 * merges them on top of the JSON shipped with the build. The fallback
 * chain is `API merged > bundled JSON > EN keys` — failure on the API
 * side is non-fatal, the user just sees the bundled defaults.
 */
async function loadBundle(locale: AppLocale): Promise<Record<string, Record<string, string>>> {
  if (!API_BASE) return {}
  try {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), 3000)
    const res = await fetch(`${API_BASE}/i18n/${APP_SCOPE}/${locale}`, { signal: ctrl.signal })
    clearTimeout(timer)
    if (!res.ok) return {}
    const json = await res.json()
    return json?.data?.pages ?? {}
  } catch {
    return {}
  }
}

/**
 * Initializes i18n synchronously using the bundled JSON resources, then
 * fires off a non-blocking fetch for the admin-edited overrides on top.
 * Keeping the initial init synchronous matters: any component that calls
 * `useTranslation` during the first render needs the i18n instance to be
 * ready, otherwise the hook throws (e.g. iterating over an undefined
 * `ns` array). Synchronous init = bundled defaults render immediately,
 * API overrides swap in asynchronously when they land.
 */
export function initI18n(): void {
  const initial = readLocale()

  i18n.use(initReactI18next).init({
    resources: {
      en: { ...baseBundles.en },
      id: { ...baseBundles.id },
      tet: { ...baseBundles.tet },
    },
    lng: initial,
    fallbackLng: 'id',
    interpolation: { escapeValue: false },
    ns: ['common'],
    defaultNS: 'common',
    returnNull: false,
    react: { useSuspense: false },
  })

  // Best-effort overlay — failure is silent, fallbacks already rendered.
  loadBundle(initial).then((apiBundle) => {
    for (const [page, pairs] of Object.entries(apiBundle)) {
      i18n.addResourceBundle(initial, page, pairs, true, true)
    }
  })
}

export async function changeLocale(locale: AppLocale): Promise<void> {
  if (!i18n.hasResourceBundle(locale, 'common')) {
    const base = baseBundles[locale]
    for (const [ns, pairs] of Object.entries(base)) {
      i18n.addResourceBundle(locale, ns, pairs, true, true)
    }
  }
  const overrides = await loadBundle(locale)
  for (const [page, pairs] of Object.entries(overrides)) {
    i18n.addResourceBundle(locale, page, pairs, true, true)
  }
  await i18n.changeLanguage(locale)
}

export default i18n
